/**
 * Lightweight force layout: radial anchors + link springs + repulsion.
 */

/**
 * @typedef {object} SimNode
 * @property {string} id
 * @property {number} x
 * @property {number} y
 * @property {number} vx
 * @property {number} vy
 * @property {number} tx
 * @property {number} ty
 * @property {boolean} pinned
 */

/**
 * @param {!Array<string>} nodeIds
 * @param {!Object<string, {x: number, y: number}>} anchorPositions
 * @param {string|null} rootKey
 * @param {!Object<string, {x: number, y: number}>} [previousPositions]
 * @param {!Object<string, string>} [parentByNode]
 * @return {!Array<SimNode>}
 */
export function initSimulation(
  nodeIds,
  anchorPositions,
  rootKey,
  previousPositions = {},
  parentByNode = {},
) {
  return nodeIds.map(id => {
    const anchor = anchorPositions[id] || { x: 480, y: 340 };
    const prev = previousPositions[id];
    const parentId = parentByNode[id];
    const parentPos = parentId ? previousPositions[parentId] || anchorPositions[parentId] : null;
    let x = anchor.x;
    let y = anchor.y;
    if (prev) {
      x = prev.x;
      y = prev.y;
    } else if (parentPos) {
      x = parentPos.x + (Math.random() - 0.5) * 36;
      y = parentPos.y + (Math.random() - 0.5) * 36;
    } else {
      x = anchor.x + (Math.random() - 0.5) * 18;
      y = anchor.y + (Math.random() - 0.5) * 18;
    }
    return {
      id,
      x,
      y,
      vx: 0,
      vy: 0,
      tx: anchor.x,
      ty: anchor.y,
      pinned: id === rootKey,
    };
  });
}

/**
 * @param {!Array<SimNode>} nodes
 * @param {!Array<{source: string, target: string}>} edges
 * @param {object} [options]
 */
export function simulationTick(nodes, edges, options = {}) {
  const repulsion = options.repulsion ?? 5200;
  const linkDistance = options.linkDistance ?? 120;
  const linkStrength = options.linkStrength ?? 0.052;
  const anchorStrength = options.anchorStrength ?? 0.034;
  const damping = options.damping ?? 0.84;

  const byId = new Map(nodes.map(node => [node.id, node]));

  nodes.forEach(node => {
    node.vx *= damping;
    node.vy *= damping;
  });

  for (let i = 0; i < nodes.length; i += 1) {
    for (let j = i + 1; j < nodes.length; j += 1) {
      const a = nodes[i];
      const b = nodes[j];
      let dx = b.x - a.x;
      let dy = b.y - a.y;
      let distSq = dx * dx + dy * dy;
      if (distSq < 0.01) {
        distSq = 0.01;
        dx = (Math.random() - 0.5) * 0.1;
        dy = (Math.random() - 0.5) * 0.1;
      }
      const force = repulsion / distSq;
      const dist = Math.sqrt(distSq);
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      if (!a.pinned) {
        a.vx -= fx;
        a.vy -= fy;
      }
      if (!b.pinned) {
        b.vx += fx;
        b.vy += fy;
      }
    }
  }

  edges.forEach(edge => {
    const source = byId.get(edge.source);
    const target = byId.get(edge.target);
    if (!source || !target) {
      return;
    }
    let dx = target.x - source.x;
    let dy = target.y - source.y;
    const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 0.01);
    const delta = dist - linkDistance;
    const force = delta * linkStrength;
    const fx = (dx / dist) * force;
    const fy = (dy / dist) * force;
    if (!source.pinned) {
      source.vx += fx;
      source.vy += fy;
    }
    if (!target.pinned) {
      target.vx -= fx;
      target.vy -= fy;
    }
  });

  nodes.forEach(node => {
    if (node.pinned) {
      node.x = node.tx;
      node.y = node.ty;
      node.vx = 0;
      node.vy = 0;
      return;
    }
    node.vx += (node.tx - node.x) * anchorStrength;
    node.vy += (node.ty - node.y) * anchorStrength;
    node.x += node.vx;
    node.y += node.vy;
  });
}

/**
 * @param {!Array<SimNode>} nodes
 * @return {number}
 */
export function simulationEnergy(nodes) {
  return nodes.reduce((sum, node) => sum + node.vx * node.vx + node.vy * node.vy, 0);
}

/**
 * @param {!Array<SimNode>} nodes
 * @return {!Object<string, {x: number, y: number}>}
 */
export function positionsFromSimulation(nodes) {
  /** @type {Object<string, {x: number, y: number}>} */
  const positions = {};
  nodes.forEach(node => {
    positions[node.id] = { x: node.x, y: node.y };
  });
  return positions;
}

/**
 * Parent map for newly spawned nodes (first incoming edge wins).
 *
 * @param {!Array<{source: string, target: string}>} edges
 * @return {!Object<string, string>}
 */
export function parentByTarget(edges) {
  /** @type {Object<string, string>} */
  const map = {};
  edges.forEach(edge => {
    if (!map[edge.target]) {
      map[edge.target] = edge.source;
    }
  });
  return map;
}
